/**
 * Dashboards — AIP role-specific dashboard hub.
 *
 * Thin shell. Each role view lives under client/src/pages/dashboards/.
 *
 * Routes:
 *   /dashboards-preview  — public, no auth (Bryan review / prospect demos)
 *   /dashboards          — auth-gated original (preserved, don't break)
 *
 * Roles: all | marketing | bdr | se | ae | management | executive
 *
 * MOCK · Design Only watermark stays top-right.
 *
 * Batch 1 (live): ae, management
 * Batch 2 (live): executive, bdr
 * Batch 3 (coming): marketing, se
 * Intake: /intake route
 */

import { useEffect, useState, Suspense, lazy } from "react";
import { Link } from "wouter";
import { api, getSession, getCurrentTenant, supabase } from "@/lib/session";
import { hasExecutiveAccess } from "@/lib/rbac";
import { loadStore, totalWeightedForecast, fmt$ } from "@/lib/intake-store";
import Mi6BrandFooter from "@/components/dashboards/Mi6BrandFooter";
import SecondaryNav, { type SecondaryNavRole } from "@/components/dashboards/SecondaryNav";
import { useTour } from "@/lib/tour-store";
import { useToast } from "@/hooks/use-toast";
import OperatorShell from "@/components/operator/OperatorShell";

// Tour button — disabled until tour flow is polished. Bryan: "if we can't
// get it to flow right I'd rather not have it." Re-enable by removing the
// early return below once the tour stops are tightened.
function TakeTourPill() {
  return null;
  // eslint-disable-next-line @typescript-eslint/no-unreachable, no-unreachable
  const u = getSession();
  const { startTour } = useTour();
  if (!u || u.email !== "attilius@mi6group.com") return null;
  return (
    <button
      type="button"
      onClick={() => startTour()}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0B0B0F] hover:bg-black text-white text-[11px] font-bold uppercase tracking-wide transition-colors shadow-sm whitespace-nowrap"
      data-tour="take-tour-button"
    >
      Take the tour
    </button>
  );
}

// ─── Lazy-load role views ─────────────────────────────────────────────────────

const AeView       = lazy(() => import("./dashboards/AeView"));
const DailyView    = lazy(() => import("./dashboards/DailyView"));
const ManagerView  = lazy(() => import("./dashboards/ManagerView"));
const AllView      = lazy(() => import("./dashboards/AllView"));

// Batch 2 & 3 — lazy import but show "coming soon" overlay until ready
const ExecutiveView = lazy(() => import("./dashboards/ExecutiveView"));
const BdrView       = lazy(() => import("./dashboards/BdrView"));
const MarketingView = lazy(() => import("./dashboards/MarketingView"));
const SeView        = lazy(() => import("./dashboards/SeView"));

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Account {
  id: number;
  name: string;
  industry: string;
  estArr: number;
  stage: string;
  score: number;
  bucket: string | null;
  updatedAt: number;
  ownerName?: string;
  greenCount?: number;
  alerts?: string[];
}

export type RoleKey =
  | "all"
  | "marketing"
  | "bdr"
  | "se"
  | "ae"
  | "daily"
  | "management"
  | "executive";

// ─── Role config ──────────────────────────────────────────────────────────────

// Mi6 locked palette — all active states use Mi6 black (#0B0B0F)
const MI6_ACTIVE = "text-white border-[#0B0B0F]" ; // bg applied inline for Tailwind JIT

const ROLES: {
  key: RoleKey;
  label: string;
  activeClass: string;
  dotColor: string;
  batch: 1 | 2 | 3;
  hidden?: boolean;  // hidden from the role-picker strip (sub-views)
}[] = [
  { key: "all",        label: "All Roles",        activeClass: "bg-[#0B0B0F] text-white border-[#0B0B0F]", dotColor: "#0B0B0F", batch: 1 },
  { key: "ae",         label: "AE",               activeClass: "bg-[#0B0B0F] text-white border-[#0B0B0F]", dotColor: "#0B0B0F", batch: 1 },
  { key: "daily",      label: "Daily AE",         activeClass: "bg-[#0B0B0F] text-white border-[#0B0B0F]", dotColor: "#0B0B0F", batch: 1, hidden: true },
  { key: "management", label: "Management",        activeClass: "bg-[#0B0B0F] text-white border-[#0B0B0F]", dotColor: "#0B0B0F", batch: 1 },
  { key: "executive",  label: "Executive",         activeClass: "bg-[#0B0B0F] text-white border-[#0B0B0F]", dotColor: "#0B0B0F", batch: 1 },
  { key: "bdr",        label: "BDR",               activeClass: "bg-[#0B0B0F] text-white border-[#0B0B0F]", dotColor: "#0B0B0F", batch: 1 },
  { key: "marketing",  label: "Marketing",         activeClass: "bg-[#0B0B0F] text-white border-[#0B0B0F]", dotColor: "#0B0B0F", batch: 1 },
  { key: "se",         label: "Sales Engineering", activeClass: "bg-[#0B0B0F] text-white border-[#0B0B0F]", dotColor: "#0B0B0F", batch: 1 },
];

// Mi6 unified bg — same neutral across ALL roles
const PAGE_BG: Record<RoleKey, string> = {
  all:        "#FAFAF7",
  ae:         "#FAFAF7",
  daily:      "#FAFAF7",
  management: "#FAFAF7",
  executive:  "#FAFAF7",
  bdr:        "#FAFAF7",
  marketing:  "#FAFAF7",
  se:         "#FAFAF7",
};

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function ViewSkeleton() {
  return (
    <div className="space-y-4 animate-pulse pt-4">
      <div className="h-40 rounded-xl bg-neutral-200/60" />
      <div className="grid grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 rounded-xl bg-neutral-100" />)}
      </div>
      <div className="h-56 rounded-xl bg-neutral-100" />
      <div className="grid grid-cols-2 gap-4">
        <div className="h-44 rounded-xl bg-neutral-100" />
        <div className="h-44 rounded-xl bg-neutral-100" />
      </div>
    </div>
  );
}

// ─── Coming-soon placeholder ──────────────────────────────────────────────────

function ComingSoon({ role, batch, dotColor }: { role: string; batch: number; dotColor: string }) {
  const batchLabel = batch === 2 ? "Batch 2 — deploying next" : "Batch 3 — in progress";
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] text-center gap-4">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-2"
        style={{ background: dotColor + "22", border: `2px solid ${dotColor}44` }}>
        <div className="w-4 h-4 rounded-full animate-pulse" style={{ background: dotColor }} />
      </div>
      <div className="text-xl font-bold text-neutral-700">{role} View</div>
      <div className="text-neutral-500 text-sm max-w-xs leading-relaxed">
        This view is being built and will appear in {batchLabel}.
        AE and Management views are live now — select them above.
      </div>
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border"
        style={{ color: dotColor, borderColor: dotColor + "44", background: dotColor + "11" }}>
        {batchLabel}
      </span>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Dashboards() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  // Support ?role=executive query param + path-segment deep links for direct deep-links / screenshots
  const initialRole = (() => {
    // Hash routing: URL is /#/dashboards-preview?role=executive OR /#/dashboards/bdr
    const hash = window.location.hash; // e.g. "#/dashboards/bdr" or "#/dashboards-preview?role=ae"
    const hashQuery = hash.includes("?") ? hash.split("?")[1] : "";
    const param = (
      new URLSearchParams(window.location.search).get("role") ||
      new URLSearchParams(hashQuery).get("role")
    ) as RoleKey | null;
    if (param && ROLES.some((r) => r.key === param)) return param;
    // Path-segment fallback: /dashboards/<role>
    const pathMatch = hash.match(/dashboards\/([a-z]+)/i);
    if (pathMatch) {
      // "manager" is a URL alias for the internal "management" key
      let seg = pathMatch[1].toLowerCase() as RoleKey;
      if ((seg as string) === "manager") seg = "management";
      if (ROLES.some((r) => r.key === seg)) return seg;
    }
    // D-018: default to the logged-in user's own role, not always AE
    // W36-B: use hasExecutiveAccess so isDualRole managers (e.g. Bryan) default to executive
    const s = getSession();
    if (s?.role === "bdr") return "bdr";
    if (hasExecutiveAccess(s)) return "executive";
    if (s?.role === "manager") return "management";
    if (s?.role === "marketing") return "marketing";
    if (s?.role === "se") return "se";
    return "ae";
  })();
  const [role, setRole]         = useState<RoleKey>(initialRole); // Default to logged-in user's role

  // ── Cache-bust safeguard: re-derive role from hash on navigation ─────────
  // Fixes iPad Safari: when navigating between /dashboards/ae → /dashboards/bdr
  // without a full reload, React keeps the same Dashboards instance and the
  // initialRole IIFE doesn't re-run.  Listening to hashchange/popstate ensures
  // the tab always reflects the current URL segment.
  useEffect(() => {
    function syncRoleFromHash() {
      const hash = window.location.hash;
      const hashQuery = hash.includes("?") ? hash.split("?")[1] : "";
      const param = (
        new URLSearchParams(window.location.search).get("role") ||
        new URLSearchParams(hashQuery).get("role")
      ) as RoleKey | null;
      if (param && ROLES.some((r) => r.key === param)) {
        setRole(param);
        return;
      }
      const pathMatch = hash.match(/dashboards\/([a-z]+)/i);
      if (pathMatch) {
        // "manager" is a URL alias for the internal "management" key
        let seg = pathMatch[1].toLowerCase() as RoleKey;
        if ((seg as string) === "manager") seg = "management";
        if (ROLES.some((r) => r.key === seg)) {
          setRole(seg);
        }
      }
    }
    window.addEventListener("hashchange", syncRoleFromHash);
    window.addEventListener("popstate", syncRoleFromHash);
    return () => {
      window.removeEventListener("hashchange", syncRoleFromHash);
      window.removeEventListener("popstate", syncRoleFromHash);
    };
  }, []);

  // Read intake store for live data passthrough
  const intakeStore = loadStore();
  const intakePipelineTotal = totalWeightedForecast(intakeStore.pipeline);
  const hasIntakeData = intakeStore.pipeline.length > 0 || intakeStore.closedDeals.length > 0;

  useEffect(() => {
    (async () => {
      try {
        const data = await api<Account[]>("/api/manager/portfolio");
        setAccounts(data);
      } catch (e: any) {
        setError(e?.message ?? "Failed to load pipeline data.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const activeRoleCfg = ROLES.find((r) => r.key === role)!;
  const showIntakePill = role === "ae" || role === "daily" || role === "management";
  const { toast } = useToast();
  const currentTenant = getCurrentTenant();
  const isDemo = currentTenant?.is_demo === true;
  const [reseeding, setReseeding] = useState(false);

  // D-018: Determine if the current user can switch roles (manager/executive/demo only)
  const sessionUser = getSession();
  const canSwitchRoles = sessionUser?.role === "manager" || sessionUser?.role === "executive" ||
    sessionUser?.email === "attilius@mi6group.com" || sessionUser?.isDualRole === true;
  // D-018: roles visible in the picker — BDR/AE/SE/Marketing users see only their own role tab
  const visibleRoles = canSwitchRoles
    ? ROLES.filter((r) => !r.hidden)
    : ROLES.filter((r) => !r.hidden && r.key === role);

  async function handleDemoReset() {
    if (!window.confirm("Wipe all demo data and reload sample data?")) return;
    setReseeding(true);
    try {
      const { error } = await supabase().rpc("reseed_demo_tenant");
      if (error) throw error;
      toast({ title: "Demo data reset", description: "Sample data has been reloaded." });
      // Reload accounts
      const data = await api<Account[]>("/api/manager/portfolio").catch(() => []);
      setAccounts(data);
    } catch (e: any) {
      toast({
        title: "Reset failed",
        description: e?.message ?? "Could not reset demo data. Try again.",
        variant: "destructive",
      });
    } finally {
      setReseeding(false);
    }
  }

  return (
    <OperatorShell
      breadcrumbs={[{ label: "Home", href: "/" }, { label: "Dashboards" }]}
      pageTitle="Dashboards"
    >
      {/* D-018: Role filter strip — only shown to users who can switch roles (manager/executive/demo) */}
      {canSwitchRoles && (
        <div className="mb-5">
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <span className="text-[9px] uppercase tracking-[0.22em] text-neutral-500 font-semibold mr-1 whitespace-nowrap hidden sm:block flex-shrink-0">
              View as
            </span>
            {visibleRoles.map((r) => {
              const isActive = role === r.key || (role === "daily" && r.key === "ae");
              return (
                <button
                  key={r.key}
                  onClick={() => {
                    setRole(r.key);
                    window.location.hash = `/dashboards/${r.key}`;
                  }}
                  className={[
                    "text-[11px] px-3 py-1.5 rounded-full border transition-all duration-150 font-medium whitespace-nowrap flex-shrink-0",
                    isActive
                      ? "bg-[#0B0B0F] text-white border-[#0B0B0F]"
                      : "bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300 hover:text-neutral-800",
                  ].join(" ")}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Demo reset — only for demo tenants */}
      {isDemo && (
        <div className="mb-4">
          <button
            type="button"
            onClick={handleDemoReset}
            disabled={reseeding}
            style={{
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.04em",
              color: reseeding ? "#8E8E94" : "#DC2626",
              background: "white",
              border: "1px solid " + (reseeding ? "#E5E5E7" : "#DC2626"),
              borderRadius: "6px",
              padding: "6px 12px",
              cursor: reseeding ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
              transition: "all 0.15s",
            }}
          >
            {reseeding ? "Resetting..." : "Reset & re-seed demo"}
          </button>
        </div>
      )}

      {/* Role view content */}
      <Suspense fallback={<ViewSkeleton />}>
        {role === "all"        && <AllView accounts={accounts} loading={loading} />}
        {role === "ae"         && <AeView accounts={accounts} loading={loading} />}
        {role === "daily"      && <DailyView />}
        {role === "management" && <ManagerView />}
        {role === "executive"  && <ExecutiveView loading={loading} />}
        {role === "bdr"        && <BdrView />}
        {role === "marketing"  && <MarketingView />}
        {role === "se"         && <SeView />}
      </Suspense>
    </OperatorShell>
  );
}